import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { AIAnswerMessage } from './AIAnswerMessage';
import { neuralSearchAPI, NeuralSearchAction, Topic, Tip } from './neuralSearchAPI';
import styles from './QueryAssistantModal.module.scss';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { ReactComponent as SendIcon } from './assets/send.svg';
import { ReactComponent as NewChatIcon } from './assets/newChat.svg';
import { formatQuery } from '../../utils/formatQuery';
import { ampli } from '../../ampli';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  query?: string;
  isLoading?: boolean;
  responseId?: number;
  isError?: boolean;
  topics?: Topic[];
  tip?: Tip;
}

export interface FormattedToken {
  text: string;
  color?: string;
}

export interface TipMapping {
  title: string;
  text: string[];
  message: string;
}

export interface QueryAssistantSession {
  messages: Message[];
  sessionId?: string;
  currentQuery: string;
  originalPrompt: string;
  hasInitialQuery: boolean;
}

interface QueryAssistantModalProps {
  isVisible: boolean;
  initialQuery?: string;
  session?: QueryAssistantSession;
  onSessionUpdate?: (session: QueryAssistantSession) => void;
  onClose: () => void;
  onInsertQuery: (query: string) => void;
}

export const QueryAssistantModal: React.FC<QueryAssistantModalProps> = ({
  isVisible,
  initialQuery = '',
  session,
  onSessionUpdate,
  onClose,
  onInsertQuery,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const userId = currentUser?.id;
  const workspaceId = currentUser?.workspace?.id;

  const [messages, setMessages] = useState<Message[]>(session?.messages || []);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialQuery, setHasInitialQuery] = useState(session?.hasInitialQuery || false);
  const [sessionId, setSessionId] = useState<string | undefined>(session?.sessionId);
  const [currentQuery, setCurrentQuery] = useState<string>(session?.currentQuery || '');
  const [originalPrompt, setOriginalPrompt] = useState<string>(session?.originalPrompt || '');
  const [showOptimizeSyntax, setShowOptimizeSyntax] = useState(false);
  const [isFirstUserMessage, setIsFirstUserMessage] = useState(true);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevSessionRef = useRef(session);
  const originalPromptRef = useRef<string>(session?.originalPrompt || '');
  const prevInitialQueryRef = useRef<string>('');
  const [isNewChatModalVisible, setIsNewChatModalVisible] = useState(false);

  const generateSessionId = (): string => {
    const timestamp = Date.now();
    return `session_${userId}_${workspaceId}_${timestamp}`;
  };

  const syncSession = () => {
    if (onSessionUpdate) {
      onSessionUpdate({
        messages,
        sessionId,
        currentQuery,
        originalPrompt,
        hasInitialQuery,
      });
    }
  };

  // Reset state when session is cleared by parent (only when it changes from something to null)
  useEffect(() => {
    if (!session && prevSessionRef.current) {
      // Abort any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      setMessages([]);
      setSessionId(undefined);
      setCurrentQuery('');
      setOriginalPrompt('');
      originalPromptRef.current = '';
      prevInitialQueryRef.current = '';
      setIsFirstUserMessage(true);
      setShowOptimizeSyntax(false);
      setHasInitialQuery(false);
      setIsLoading(false);
    }
    prevSessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (!isVisible) return;

    const trimmedInitialQuery = initialQuery?.trim() || '';
    const previousInitialQuery = prevInitialQueryRef.current;

    // Clear messages if there's no initial query and no session
    if (!session && messages.length > 0 && trimmedInitialQuery.length === 0) {
      setMessages([]);
      setSessionId(undefined);
      setCurrentQuery('');
      setOriginalPrompt('');
      originalPromptRef.current = '';
      setIsFirstUserMessage(true);
      setShowOptimizeSyntax(false);
      setHasInitialQuery(false);
      syncSession();
      return;
    }

    // Handle initial query
    if (trimmedInitialQuery.length > 0) {
      // Only add new message if initial query changed
      if (trimmedInitialQuery !== previousInitialQuery) {
        // Check if last message is an assistant message with a query
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        const isLastMessageAssistantWithQuery =
          lastMessage?.role === 'assistant' && lastMessage?.query;

        // If the last assistant message already has the same query, don't add initial query message
        if (isLastMessageAssistantWithQuery && lastMessage?.query === trimmedInitialQuery) {
          setHasInitialQuery(true);
          setCurrentQuery(trimmedInitialQuery);
          setOriginalPrompt(trimmedInitialQuery);
          originalPromptRef.current = trimmedInitialQuery;
          prevInitialQueryRef.current = trimmedInitialQuery;
          return;
        }

        // Check if last message is an initial query message (assistant with query but no responseId)
        // Initial query messages don't have responseId, while backend responses do
        const isLastMessageInitialQuery =
          lastMessage?.role === 'assistant' &&
          lastMessage?.query &&
          !lastMessage?.responseId &&
          !lastMessage?.isError;

        setHasInitialQuery(true);
        setCurrentQuery(trimmedInitialQuery);
        setOriginalPrompt(trimmedInitialQuery);
        originalPromptRef.current = trimmedInitialQuery;
        setShowOptimizeSyntax(true);

        ampli.track({
          event_type: 'sr_as_opened_with_query',
          event_properties: {
            user_id: userId,
            workspace_id: workspaceId,
          },
        });

        // Show initial query as AI message without sending to backend
        const assistantMessageId = `assistant-${Date.now()}`;
        const assistantMessage: Message = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          query: trimmedInitialQuery,
          isLoading: false,
          isError: false,
        };

        setMessages((prevMessages) => {
          // If last message is an initial query, remove it and add new one
          if (isLastMessageInitialQuery) {
            return [...prevMessages.slice(0, -1), assistantMessage];
          }
          // Otherwise just add the new message
          return [...prevMessages, assistantMessage];
        });

        // Generate new session if needed
        if (!sessionId) {
          const newSessionId = generateSessionId();
          setSessionId(newSessionId);
        }

        // Initial query counts as first message, so next user input should use REFINE
        setIsFirstUserMessage(false);
        syncSession();

        // Update the ref to track this initial query
        prevInitialQueryRef.current = trimmedInitialQuery;
      }
    } else {
      // No initial query
      setHasInitialQuery(false);

      // Track opening without query (only once)
      if (previousInitialQuery === '' && messages.length === 0) {
        ampli.track({
          event_type: 'sr_as_opened',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: workspaceId,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, initialQuery]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isVisible) {
      if (messages.length > 0) {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      if (inputRef.current && !isLoading) {
        inputRef.current.focus();
      }
    }
  }, [isVisible, isLoading, messages.length]);

  const getErrorMessage = (error: any): string => {
    const errorMessages: Record<string, string> = {
      "Client error: It seems you requested an operator that isn't supported yet.":
        t("It seems you requested an operator that isn't supported yet."),
      "Client error: Asking to produce the search query in a language other than English or Ukrainian.":
        t("Asking to produce the search query in a language other than English or Ukrainian."),
      "Client error: I only work in English and Ukrainian. Try describing your search in one of these languages":
        t("I can only understand requests in English or Ukrainian. Try again using one of these languages."),
      "Client error: Please enter the topic or statement you'd like me to convert into a Boolean search query.":
        t("Please enter the topic or statement you'd like me to convert into a Boolean search query."),
      "Client error: It looks like your input is a bit too general for me to create an effective search query. Please provide additional context or specify the topic you'd like to focus on.":
        t("It looks like your input is a bit too general for me to create an effective search query. Please provide additional context or specify the topic you'd like to focus on."),
    };

    return errorMessages[error] || t('Failed to generate query. Please try again.');
  }

  const mapTip = (tip: string): TipMapping => {
    const tipMappings: Record<string, TipMapping> = {
      'DATES': {
        title: t('Tip: You can set up filters on the search page.'),
        text: [t('Use calendar to select a date or date range.')],
        message: t('It seems like you want to add filters. I work with queries only.')
      },
      'COUNTRIES': {
        title: t('Tip: You can set up filters on the search page.'),
        text: [
          t('Use Actor origin countries filter to see actors from specific countries.'),
          t('Use State-affiliated filter to see actors that are affiliated with specific countries.')
        ],
        message: t('It seems like you want to add filters. I work with queries only.')
      },
      'FILTER': {
        title: t('Tip: You can set up filters on the search page.'),
        text: [],
        message: t('It seems like you want to add filters. I work with queries only.')
      },
      'TRANSLATE': {
        title: t('Tip: You can search in multiple languages with the Multilingual search.'),
        text: [t('Find the Multilingual search toggle on the search page.')],
        message: t("I can't translate queries.")
      },
      'GENERAL': {
        title: t('Tip: You can set up filters on the search page.'),
        text: [],
        message: t('It seems like you want to add filters. I work with queries only.')
      }
    };
    return tipMappings[tip] || tipMappings['GENERAL'];
  };

  const normalizeTips = (tips?: string[]): string | undefined => {
    if (!tips || tips.length === 0) return undefined;

    // If there are multiple tips, return a general type tip
    if (tips.length > 1) {
      return 'GENERAL';
    }
  
    // Return the single tip
    return tips[0];
  };

  const handleSendMessage = async (
    text?: string,
    action?: NeuralSearchAction,
  ) => {
    const messageText = text || inputValue.trim();
    if ((!messageText && !action) || isLoading) return;

    const currentSessionId = sessionId || generateSessionId();
    if (!sessionId) {
      setSessionId(currentSessionId);
    }

    // Set originalPrompt immediately for first user message (before API call)
    // Use ref to ensure synchronous access for subsequent action calls
    let newOriginalPrompt = originalPromptRef.current;
    if (!originalPromptRef.current && !action && isFirstUserMessage) {
      newOriginalPrompt = hasInitialQuery ? initialQuery.trim() : messageText.trim();
      originalPromptRef.current = newOriginalPrompt;
      setOriginalPrompt(newOriginalPrompt);
    }

    if (messageText) {
      const userMessageId = `user-${Date.now()}`;
      const userMessage: Message = {
        id: userMessageId,
        role: 'user',
        content: messageText,
      };
      setMessages((prev) => [...prev, userMessage]);
    }

    setInputValue('');
    setIsLoading(true);

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    abortControllerRef.current = new AbortController();

    const loadingMessageId = `assistant-${Date.now()}`;
    const loadingMessage: Message = {
      id: loadingMessageId,
      role: 'assistant',
      content: '',
      isLoading: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      const originalPromptToSend = newOriginalPrompt || null;

      // Use provided action or default to GENERATE
      const actionToSend: NeuralSearchAction = action || 'GENERATE';

      const response = await neuralSearchAPI.sendPrompt(
        {
          session_id: currentSessionId,
          action: actionToSend,
          prompt: messageText,
          current_query: currentQuery || null,
          original_prompt: originalPromptToSend,
        },
        abortControllerRef.current.signal,
      );

      if (!abortControllerRef.current?.signal.aborted) {
        const newCurrentQuery = response.query;
        let normalizedTips = normalizeTips(response.tips) || undefined;

        setCurrentQuery(newCurrentQuery);

        // Mark that first user message has been sent (only on success)
        if (isFirstUserMessage && !action) {
          setIsFirstUserMessage(false);
        }

        if (!action) {
          ampli.track({
            event_type: 'sr_as_prompt_submitted',
            event_properties: {
              user_id: userId,
              workspace_id: workspaceId,
            },
          });
        }

        const assistantMessage: Message = {
          id: loadingMessageId,
          role: 'assistant',
          content: '',
          query: response.query,
          isLoading: false,
          responseId: response.id,
          isError: false,
          topics: response.topics,
          tip: normalizedTips ? mapTip(normalizedTips) : undefined,
        };

        setMessages((prev) => {
          const newMessages = prev.map((msg) =>
            msg.id === loadingMessageId ? assistantMessage : msg
          );

          // Sync with the updated values immediately using the new messages
          if (onSessionUpdate) {
            onSessionUpdate({
              messages: newMessages,
              sessionId: currentSessionId,
              currentQuery: newCurrentQuery,
              originalPrompt: newOriginalPrompt,
              hasInitialQuery,
            });
          }

          return newMessages;
        });
        if (showOptimizeSyntax) {
          setShowOptimizeSyntax(false);
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Error sending message:', error);

        // Don't mark first message as sent if it resulted in error
        // This ensures next attempt will still use GENERATE action

        const errorMessage: Message = {
          id: loadingMessageId,
          role: 'assistant',
          content:
            getErrorMessage(error.message) || t('Failed to generate query. Please try again.'),
          isLoading: false,
          isError: true,
        };
        setMessages((prev) =>
          prev.map((msg) => (msg.id === loadingMessageId ? errorMessage : msg)),
        );
      } else {
        // Remove loading message if aborted
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== loadingMessageId),
        );
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleTopicClick = (topic: Topic) => {
    ampli.track({
      event_type: 'sr_as_topic_clicked',
      event_properties: {
        user_id: userId,
        workspace_id: workspaceId,
        topic: topic.name,
      },
    });
    handleSendMessage(topic.name, 'APPLY_TOPIC');
  };

  const handleInsertQuery = (query: string) => {
    ampli.track({
      event_type: 'sr_as_query_inserted',
      event_properties: {
        user_id: userId,
        workspace_id: workspaceId,
      },
    });
    onInsertQuery(query);
    // onClose();
  };

  const handleFeedback = async (messageId: string, isHelpful: boolean) => {
    const message = messages.find((msg) => msg.id === messageId);
    if (!message?.responseId) {
      console.error('No response ID found for message:', messageId);
      return;
    }

    ampli.track({
      event_type: 'sr_as_rated',
      event_properties: {
        rating: isHelpful ? 'positive' : 'negative',
        user_id: userId,
        workspace_id: workspaceId,
      },
    });

    try {
      await neuralSearchAPI.sendFeedback({
        id: message.responseId,
        feedback: isHelpful ? 'POSITIVE' : 'NEGATIVE',
      });
    } catch (error: any) {
      console.error('Error sending feedback:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear all chat state
    setMessages([]);
    setInputValue('');
    setIsLoading(false);
    setHasInitialQuery(false);
    setCurrentQuery('');
    setOriginalPrompt('');
    originalPromptRef.current = '';
    prevInitialQueryRef.current = '';
    setIsFirstUserMessage(true);
    setShowOptimizeSyntax(false);

    // Generate new session ID
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);

    // Sync the cleared session
    if (onSessionUpdate) {
      onSessionUpdate({
        messages: [],
        sessionId: newSessionId,
        currentQuery: '',
        originalPrompt: '',
        hasInitialQuery: false,
      });
    }

    // Track analytics
    ampli.track({
      event_type: 'sr_as_new_chat',
      event_properties: {
        user_id: userId,
        workspace_id: workspaceId,
      },
    });
  };

  const handleClose = () => {
    syncSession();
    ampli.track({
      event_type: 'sr_as_closed',
      event_properties: {
        user_id: userId,
        workspace_id: workspaceId,
      },
    });
    onClose();
  };

  const renderMessage = (message: Message) => {
    if (message.role === 'user') {
      return (
        <div key={message.id} className={styles.messageUser}>
          <div className={styles.messageContent}>{message.content}</div>
        </div>
      );
    }

    if (message.isLoading) {
      return (
        <div key={message.id} className={styles.messageAssistant}>
          <div className={styles.messageContent}>
            <div className={styles.loadingState}>
              <div className={styles.loader}></div>
              <span className={styles.loadingText}>
                {t('Thinking')}...
              </span>
            </div>
          </div>
        </div>
      );
    }

    const assistantMessages = messages.filter(
      (msg) => msg.role === 'assistant' && !msg.isLoading && !msg.isError && msg.query
    );
    const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
    const isLastAssistantMessage = message.id === lastAssistantMessage?.id;
    const isFirstAssistantMessage = assistantMessages.indexOf(message) === 0;

    const showOptimize = showOptimizeSyntax && isLastAssistantMessage;

    // Show topics on first backend response
    const showTopics = isFirstAssistantMessage && !!message.topics?.length;

    return (
      <AIAnswerMessage
        key={message.id}
        messageId={message.id}
        query={message.query}
        content={message.content}
        isError={message.isError}
        showOptimizeSyntax={showOptimize}
        topics={message.topics}
        tip={message.tip}
        showTopics={showTopics}
        onInsertQuery={handleInsertQuery}
        onTopicClick={handleTopicClick}
        onFeedback={handleFeedback}
        formatQuery={formatQuery}
      />
    );
  };

  const content = (
    <div className={styles.queryAssistant}>
      <div className={styles.chatWindow}>
        {messages.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateTitle}>
              {`${t('Welcome')}, ${currentUser?.first_name || t('User')}`}
            </p>
            <p className={styles.emptyStateText}>
              {t('I’ll help you build a search query')}
            </p>
            <p className={styles.emptyStateHint}>
              {t(
                'Write in your own words, for example: create a query about Trump trade war',
              )}
            </p>
          </div>
        )}
        {messages.map(renderMessage)}
        <div ref={chatEndRef} />
      </div>
    </div>
  );

  const footer = (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <textarea
          ref={inputRef}
          className={styles.input}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={t('Describe the query you want to build')}
          rows={1}
          disabled={isLoading}
        />
        <button
          className={styles.sendButton}
          onClick={() => handleSendMessage()}
          disabled={!inputValue.trim() || isLoading}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );

  const title = (
    <div className={styles.modalTitle}>
      {t('Query Assistant - Beta')}
      <button
        className={styles.newChatButton}
        onClick={()=> setIsNewChatModalVisible(true)}
        disabled={messages.length === 0}
      >
        <NewChatIcon />
        {t('New chat')}
      </button>
    </div>
  )

  return (
    <>
      <Modal
        isVisible={isVisible}
        title={title}
        content={content}
        footer={footer}
        onClose={handleClose}
        className={styles.queryAssistantModal}
      />

      <Modal
        isVisible={isNewChatModalVisible}
        title={t('New chat')}
        content={<div className={styles.newChatModalContent}>
          {t('This will delete your existing chat and start a new one.')}
        </div>}
        footer={
          <div className={styles.newChatModalFooter}>
            <button className={styles.cancelButton} onClick={() => {setIsNewChatModalVisible(false)}}>
              {t('Cancel')}
            </button>
            <button className={styles.confirmButton} onClick={() => {
              handleNewChat();
              setIsNewChatModalVisible(false);
            }}>
              {t('Create')}
            </button>
          </div>
        }
        onClose={() => {setIsNewChatModalVisible(false)}}
        className={styles.newChatModal}
      />
    </>
  );
};
